import gql from 'graphql-tag';

import GraphQLClient, { GraphQLOperationFailed } from '../../adapter/GraphQLClient';
import ModificationRequestItemInput from '../../entity/ModificationRequestItemsInput';
import {
  CreationDetailFragment,
  ModificationRequestFeeType,
  ModificationRequestForDetailFragment,
  ModificationRequestItemForDetailFragment,
  ModificationRequestItemIssueCommentForDetailFragment,
  ModificationRequestItemIssueForDetailFragment,
} from './../../generated/graphql';
import {
  creationDetailFragment,
  modificationRequestForDetailFragment,
  modificationRequestItemForDetailFragment,
  modificationRequestItemIssueCommentForDetailFragment,
  modificationRequestItemIssueForDetailFragment,
} from './CreationWebApi';

class ModificationRequestWebApi {
  private readonly graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public createModificationRequestAsDraft = async (
    targetDeliverableId: string,
  ): Promise<ModificationRequestForDetailFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${modificationRequestForDetailFragment}
          mutation createModificationRequestAsDraft($targetDeliverableId: ID!) {
            createModificationRequestAsDraft(targetDeliverableId: $targetDeliverableId) {
              ...ModificationRequestForDetail
            }
          }
        `,
        {
          targetDeliverableId,
        },
      );

      return result.createModificationRequestAsDraft as ModificationRequestForDetailFragment;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'ExpiredModificationRequestError') {
        throw new ExpiredModificationRequestError();
      }

      if (err instanceof GraphQLOperationFailed && err.type === 'AlreadyModificationRequestedError') {
        throw new AlreadyModificationRequestedError();
      }

      throw err;
    }
  };

  public submitModificationRequest = async (
    modificationRequestId: string,
    feeType?: ModificationRequestFeeType | null,
  ): Promise<ModificationRequestForDetailFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${modificationRequestForDetailFragment}
          mutation submitModificationRequest($modificationRequestId: ID!, $feeType: ModificationRequestFeeType) {
            submitModificationRequest(modificationRequestId: $modificationRequestId, feeType: $feeType) {
              ...ModificationRequestForDetail
            }
          }
        `,
        { modificationRequestId, feeType },
      );

      return result.submitModificationRequest as ModificationRequestForDetailFragment;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'ExpiredModificationRequestError') {
        throw new ExpiredModificationRequestError();
      }

      if (err instanceof GraphQLOperationFailed && err.type === 'AlreadyModificationRequestedError') {
        throw new AlreadyModificationRequestedError();
      }

      throw err;
    }
  };

  public cancelCreatingDraftModificationRequest = async (
    modificationRequestId: string,
  ): Promise<CreationDetailFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${creationDetailFragment}
          mutation cancelCreatingDraftModificationRequest($modificationRequestId: ID!) {
            cancelCreatingDraftModificationRequest(modificationRequestId: $modificationRequestId) {
              ...CreationDetail
            }
          }
        `,
        { modificationRequestId },
      );

      return result.cancelCreatingDraftModificationRequest as CreationDetailFragment;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'AlreadyModificationRequestedError') {
        throw new AlreadyModificationRequestedError();
      }

      throw err;
    }
  };

  public createModificationRequestItem = async (
    modificationRequestId: string,
    body: ModificationRequestItemInput,
  ): Promise<ModificationRequestItemForDetailFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${modificationRequestItemForDetailFragment}
          mutation createModificationRequestItem($modificationRequestId: ID!, $body: ModificationRequestItemsInput!) {
            createModificationRequestItem(modificationRequestId: $modificationRequestId, body: $body) {
              ...ModificationRequestItemForDetail
            }
          }
        `,
        { modificationRequestId, body },
      );

      return result.createModificationRequestItem as ModificationRequestItemForDetailFragment;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'ModificationRequestNotDraftError') {
        throw new ModificationRequestNotDraftError();
      }

      throw err;
    }
  };

  public editModificationRequestItem = async (
    modificationRequestItemId: string,
    body: ModificationRequestItemInput,
  ): Promise<ModificationRequestItemForDetailFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${modificationRequestItemForDetailFragment}
          mutation editModificationRequestItem($modificationRequestItemId: ID!, $body: ModificationRequestItemsInput!) {
            editModificationRequestItem(modificationRequestItemId: $modificationRequestItemId, body: $body) {
              ...ModificationRequestItemForDetail
            }
          }
        `,
        { modificationRequestItemId, body },
      );

      return result.editModificationRequestItem as ModificationRequestItemForDetailFragment;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'ModificationRequestNotDraftError') {
        throw new ModificationRequestNotDraftError();
      }

      throw err;
    }
  };

  public deleteModificationRequestItem = async (
    modificationRequestItemId: string,
  ): Promise<ModificationRequestForDetailFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${modificationRequestForDetailFragment}
          mutation deleteModificationRequestItem($modificationRequestItemId: ID!) {
            deleteModificationRequestItem(modificationRequestItemId: $modificationRequestItemId) {
              ...ModificationRequestForDetail
            }
          }
        `,
        { modificationRequestItemId },
      );

      return result.deleteModificationRequestItem as ModificationRequestForDetailFragment;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'ModificationRequestNotDraftError') {
        throw new ModificationRequestNotDraftError();
      }

      throw err;
    }
  };

  public addModificationRequestItemAsset = async (
    modificationRequestItemId: string,
    assetId: string,
  ): Promise<boolean> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          mutation addModificationRequestItemAsset($modificationRequestItemId: ID!, $assetId: String!) {
            addModificationRequestItemAsset(modificationRequestItemId: $modificationRequestItemId, assetId: $assetId)
          }
        `,
        { modificationRequestItemId, assetId },
      );
      return result.addModificationRequestItemAsset;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'ModificationRequestNotDraftError') {
        throw new ModificationRequestNotDraftError();
      }

      throw err;
    }
  };

  public deleteModificationRequestItemAssets = async (
    modificationRequestItemId: string,
    assetIds: string[],
  ): Promise<boolean> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          mutation deleteModificationRequestItemAssets($modificationRequestItemId: ID!, $assetIds: [String!]!) {
            deleteModificationRequestItemAssets(
              modificationRequestItemId: $modificationRequestItemId
              assetIds: $assetIds
            )
          }
        `,
        { modificationRequestItemId, assetIds },
      );
      return result.deleteModificationRequestItemAsset;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'ModificationRequestNotDraftError') {
        throw new ModificationRequestNotDraftError();
      }

      throw err;
    }
  };

  public createModificationRequestItemIssue = async (
    modificationRequestItemId: string,
    name: string,
    body: string,
    bucketId: string | null,
    assetIds: Array<string>,
  ): Promise<ModificationRequestItemIssueForDetailFragment> => {
    const result = await this.graphqlClient.query(
      gql`
        ${modificationRequestItemIssueForDetailFragment}
        mutation createModificationRequestItemIssue(
          $modificationRequestItemId: ID!
          $name: String!
          $body: String!
          $bucketId: String
          $assetIds: [String!]!
        ) {
          createModificationRequestItemIssue(
            modificationRequestItemId: $modificationRequestItemId
            name: $name
            body: $body
            bucketId: $bucketId
            assetIds: $assetIds
          ) {
            ...ModificationRequestItemIssueForDetail
          }
        }
      `,
      { modificationRequestItemId, name, body, bucketId, assetIds },
    );

    return result.createModificationRequestItemIssue as ModificationRequestItemIssueForDetailFragment;
  };

  public createModificationRequestIssueComment = async (
    issueId: string,
    body: string,
    bucketId: string | null,
    assetIds: Array<string>,
  ): Promise<ModificationRequestItemIssueCommentForDetailFragment> => {
    // eslint-disable-next-line no-useless-catch
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${modificationRequestItemIssueCommentForDetailFragment}
          mutation createModificationRequestItemIssueComment(
            $issueId: ID!
            $body: String!
            $bucketId: String
            $assetIds: [String!]!
          ) {
            createModificationRequestItemIssueComment(
              issueId: $issueId
              body: $body
              bucketId: $bucketId
              assetIds: $assetIds
            ) {
              ...ModificationRequestItemIssueCommentForDetail
            }
          }
        `,
        { issueId, body, bucketId, assetIds },
      );

      return result.createModificationRequestItemIssueComment as ModificationRequestItemIssueCommentForDetailFragment;
    } catch (err) {
      throw err;
    }
  };

  public resolveModificationRequestIssue = async (modificationRequestIssueId: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation resolveModificationRequestIssue($issueId: ID!) {
          resolveModificationRequestIssue(issueId: $issueId)
        }
      `,
      { issueId: modificationRequestIssueId },
    );
  };

  public reopenModificationRequestIssue = async (
    modificationRequestIssueId: string,
  ): Promise<ModificationRequestItemIssueForDetailFragment> => {
    const result = await this.graphqlClient.query(
      gql`
        ${modificationRequestItemIssueForDetailFragment}
        mutation reopenModificationRequestIssue($issueId: ID!) {
          reopenModificationRequestIssue(issueId: $issueId) {
            ...ModificationRequestItemIssueForDetail
          }
        }
      `,
      { issueId: modificationRequestIssueId },
    );

    return result.reopenModificationRequestIssue as ModificationRequestItemIssueForDetailFragment;
  };

  public approveModificationRequest = async (
    modificationRequestId: string,
  ): Promise<ModificationRequestForDetailFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${modificationRequestForDetailFragment}
          mutation approveModificationRequest($modificationRequestId: ID!) {
            approveModificationRequest(modificationRequestId: $modificationRequestId) {
              ...ModificationRequestForDetail
            }
          }
        `,
        { modificationRequestId },
      );

      return result.approveModificationRequest as ModificationRequestForDetailFragment;
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'AlreadyApprovedModificationRequestError') {
        throw new AlreadyApprovedModificationRequestError();
      }

      throw err;
    }
  };
}

export class ExpiredModificationRequestError extends Error {
  public readonly name = 'ExpiredModificationRequestError';
  public readonly message = 'Modification request is expired.';

  constructor() {
    super();
  }
}

export class AlreadyModificationRequestedError extends Error {
  public readonly name = 'AlreadyModificationRequestedError';
  public readonly message = 'Modification request is already sent.';

  constructor() {
    super();
  }
}

export class AlreadyApprovedModificationRequestError extends Error {
  public readonly name = 'AlreadyApprovedModificationRequestError';
  public readonly message = 'Modification request is already approved.';

  constructor() {
    super();
  }
}

export class ModificationRequestNotDraftError extends Error {
  public readonly name = 'ModificationRequestNotDraftError';
  public readonly message = 'Modification request is not draft.';

  constructor() {
    super();
  }
}

export default ModificationRequestWebApi;
